import { createContext, useContext, useState, useEffect } from "react";
import { getSession, setSession, clearSession, normalizeMember } from "@/lib/AuthSession";
import { base44, supabase } from "@/api/base44Client";
import { setGlobalTimezone } from "@/lib/timeUtils";
import { getDeviceId, getCurrentDeviceInfo, syncActiveSessions } from "@/lib/deviceSession";
import toast from "react-hot-toast";

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [member, setMember] = useState(() => getSession());
  const [companySettings, setCompanySettings] = useState(() => {
    // Try to restore from sessionStorage for instant availability
    try {
      const cached = sessionStorage.getItem('companySettings');
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });

  const login = (memberData) => {
    const normalized = normalizeMember(memberData);
    setSession(normalized);
    setMember(normalized);
  };

  const logout = async (isRemoteTerminated = false) => {
    try {
      const currentMem = member || getSession();
      const myDeviceId = getDeviceId();
      if (!isRemoteTerminated && currentMem?.id && myDeviceId) {
        const { data } = await supabase.from('TeamMember').select('data').eq('id', currentMem.id).limit(1);
        if (data && data[0]?.data) {
          const activeSessions = (data[0].data.active_sessions || []).filter(s => s.device_id !== myDeviceId);
          await supabase.from('TeamMember').update({
            data: { ...data[0].data, active_sessions: activeSessions },
            updated_date: new Date().toISOString(),
          }).eq('id', currentMem.id);
        }
      }
    } catch (e) {
      console.warn("Failed to clear device session on logout:", e);
    }
    localStorage.removeItem('satala_office_device_registered');
    sessionStorage.removeItem('last_device_session_sync');
    clearSession();
    setMember(null);
    if (window.location.pathname.startsWith('/office')) {
      window.location.href = "/login-tim";
    }
  };

  // Fetch company settings globally once so all roles can access logo/stempel/ttd
  const fetchCompanySettings = async () => {
    try {
      const list = await base44.entities.CompanySettings.list();
      const settings = list?.[0] || {};
      setCompanySettings(settings);
      // Cache to sessionStorage for faster subsequent page loads
      try { sessionStorage.setItem('companySettings', JSON.stringify(settings)); } catch {}
    } catch (e) {
      console.warn("Failed to fetch company settings:", e);
    }
  };

  const refreshMember = async () => {
    const currentMem = member || getSession();
    if (!currentMem?.id) return;
    if (localStorage.getItem('satala_dev_sandbox_mode') === 'true') return;
    try {
      const { data, error } = await supabase
        .from('TeamMember')
        .select('id, data, created_date, updated_date')
        .eq('id', currentMem.id)
        .limit(1);
      
      if (error) {
        // Supabase error occurred (network down, rate limited, table locked, etc.)
        // DO NOT log out the user under transient network errors!
        console.warn("Failed to check user status due to db error:", error.message);
        return;
      }

      if (data && data.length > 0) {
        const row = data[0];
        const isDeleted = Boolean(row.data?.deleted_at) || 
                          row.data?.status === 'inactive' || 
                          row.data?.status === 'nonaktif' || 
                          row.data?.status === 'deleted';

        if (isDeleted) {
          console.warn("[Security] Akun tim telah dinonaktifkan atau dihapus. Mengakhiri sesi...");
          logout();
          return;
        }

        // Cek apakah sesi perangkat ini diputuskan secara paksa dari perangkat lain
        const myDeviceId = getDeviceId();
        const wasEverRegistered = localStorage.getItem('satala_office_device_registered') === 'true';
        const activeSessions = row.data?.active_sessions;

        if (wasEverRegistered && Array.isArray(activeSessions)) {
          const isStillAllowed = activeSessions.some(s => s.device_id === myDeviceId);
          if (!isStillAllowed) {
            console.warn("[Security] Sesi perangkat ini telah diputuskan dari perangkat lain.");
            toast.error("Sesi perangkat Anda telah diakhiri dari perangkat lain.");
            logout(true);
            return;
          }
        }

        // Sinkronisasi perangkat saat ini ke active_sessions (heartbeat per 5 menit atau saat pertama kali)
        const lastSync = parseInt(sessionStorage.getItem('last_device_session_sync') || '0', 10);
        const nowTime = Date.now();
        if (nowTime - lastSync > 5 * 60 * 1000 || !wasEverRegistered) {
          const currentDev = getCurrentDeviceInfo();
          const updatedSessions = syncActiveSessions(activeSessions, currentDev);
          sessionStorage.setItem('last_device_session_sync', nowTime.toString());
          localStorage.setItem('satala_office_device_registered', 'true');

          supabase.from('TeamMember').update({
            data: {
              ...row.data,
              active_sessions: updatedSessions,
            },
            updated_date: new Date().toISOString(),
          }).eq('id', row.id).then(() => {}).catch(() => {});
        }

        // User exists and is active, update session
        const updated = normalizeMember({ ...row.data, id: row.id, created_date: row.created_date, updated_date: row.updated_date });
        setSession(updated);
        setMember(updated);
        if (updated?.timezone) {
          setGlobalTimezone(updated.timezone);
        }
      } else if (data && data.length === 0) {
        // Database explicitly returned 0 rows, meaning the user was hard deleted!
        console.warn("[Security] Akun tim tidak lagi ditemukan di database. Mengakhiri sesi...");
        logout();
      }
    } catch (e) {
      console.warn("Network error during session refresh:", e);
    }
  };

  const checkAutoCompleteOrders = async () => {
    try {
      // Find orders that are 'shipping'
      const shippingOrders = await base44.entities.ProductionOrder.filter({ status: 'shipping' });
      if (!shippingOrders || shippingOrders.length === 0) return;

      const now = new Date();
      const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
      
      for (const order of shippingOrders) {
        if (order.shipped_at) {
          const shippedDate = new Date(order.shipped_at);
          if (now - shippedDate > SEVEN_DAYS_MS) {
            // Auto complete!
            await base44.entities.ProductionOrder.update(order.id, { 
              status: 'done', 
              auto_completed: true,
              auto_completed_at: new Date().toISOString()
            });
            
            // Update ClientPO invoice status
            if (order.order_number) {
              try {
                await base44.entities.ClientPO.update(order.order_number, { statusProduksi: 'SUDAH SAMPAI' });
              } catch (e) {}
            }
          }
        }
      }
    } catch (e) {
      console.warn("Failed to auto-complete shipping orders:", e);
    }
  };

  const autoCleanupFaceImages = async () => {
    try {
      // Hanya berjalan jika ada member (orang sudah login) untuk mengurangi request public
      if (!member?.id) return;

      // OPTIMASI: Pastikan pembersihan hanya dilakukan MAKSIMAL 1 kali sehari per perangkat
      // untuk mencegah aplikasi menjadi lambat (berat) setiap kali di-refresh.
      const lastCleanup = localStorage.getItem('last_face_cleanup');
      const now = new Date();
      if (lastCleanup && (now.getTime() - parseInt(lastCleanup)) < 24 * 60 * 60 * 1000) {
        return; // Belum lewat 24 jam, batalkan proses agar aplikasi tetap cepat
      }

      const allLogs = await base44.entities.AttendanceLog.list();
      if (!allLogs || allLogs.length === 0) return;

      const ONE_DAY_MS = 24 * 60 * 60 * 1000;
      let count = 0;

      for (const log of allLogs) {
        // Jika log punya face_image dan umurnya lebih dari 1 hari
        if (log.face_image) {
          const logDate = new Date(log.timestamp || log.created_date || log.updated_date);
          if (now.getTime() - logDate.getTime() > ONE_DAY_MS) {
            await base44.entities.AttendanceLog.update(log.id, { face_image: null });
            count++;
          }
        }
      }
      
      // Simpan waktu terakhir pembersihan
      localStorage.setItem('last_face_cleanup', now.getTime().toString());
      
      if (count > 0) {
        console.log(`Auto-cleaned ${count} old attendance face images.`);
      }
    } catch (e) {
      console.warn("Failed to auto cleanup face images:", e);
    }
  };

  useEffect(() => {
    refreshMember();
    fetchCompanySettings();
    checkAutoCompleteOrders();
    autoCleanupFaceImages();

    // Re-verify immediately saat tab dibuka kembali dengan pengaman Throttle (Zero Overhead)
    let lastChecked = Date.now();
    const handleFocus = () => {
      if (document.hidden) return;
      const now = Date.now();
      // Throttle: Jangan request jika sudah dicek dalam 30 detik terakhir
      if (now - lastChecked < 30000) return;
      lastChecked = now;
      refreshMember();
    };
    window.addEventListener('visibilitychange', handleFocus);
    window.addEventListener('focus', handleFocus);

    // Periodic heartbeat verification tiap 30 menit (HANYA saat tab aktif)
    const interval = setInterval(() => {
      if (document.hidden) return;
      lastChecked = Date.now();
      refreshMember();
    }, 30 * 60 * 1000);

    return () => {
      window.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [member?.id]);

  return (
    <SessionContext.Provider value={{ member, login, logout, refreshMember, companySettings, refreshCompanySettings: fetchCompanySettings }}>
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be inside SessionProvider");
  return ctx;
};